import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { DAY_ORDER, DAY_NAMES } from "@/lib/constants";

export default function HoursEditor() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/hours").then((r) => setSettings(r.data)).catch(() => toast.error("Errore nel caricamento"));
  }, []);

  const updateDay = (key, patch) =>
    setSettings((s) => ({ ...s, days: { ...s.days, [key]: { ...s.days[key], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/hours", settings);
      toast.success("Orari aggiornati sul sito");
    } catch (err) {
      toast.error(err.response?.status === 401 ? "Sessione scaduta: esci e accedi di nuovo" : "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <p className="font-mono-alt text-xs uppercase tracking-[0.25em] text-stone-400">Caricamento…</p>;
  }

  return (
    <div data-testid="admin-hours-editor">
      <h1 className="font-display text-3xl font-semibold text-ink sm:text-4xl">Orari di apertura</h1>
      <p className="mt-2 text-sm text-stone-500">
        Le modifiche sono visibili immediatamente sul sito pubblico.
      </p>

      <div className="mt-8 overflow-hidden rounded-2xl border border-bordeaux/15 bg-white">
        {DAY_ORDER.map((k) => {
          const d = settings.days[k];
          return (
            <div
              key={k}
              data-testid={`admin-hours-row-${k}`}
              className="flex flex-wrap items-center gap-4 border-b border-bordeaux/10 px-5 py-4 last:border-0 sm:px-7"
            >
              <span className="w-28 font-display text-xl font-medium text-ink">{DAY_NAMES[k]}</span>
              <label className="flex items-center gap-2 text-sm text-stone-500">
                <input
                  data-testid={`admin-hours-closed-${k}`}
                  type="checkbox"
                  checked={d.closed}
                  onChange={(e) => updateDay(k, { closed: e.target.checked })}
                  className="h-4 w-4 accent-[#AB182B]"
                />
                Chiuso
              </label>
              {!d.closed && (
                <div className="flex items-center gap-2 font-mono-alt text-sm">
                  <input
                    data-testid={`admin-hours-open-${k}`}
                    type="time"
                    value={d.open}
                    onChange={(e) => updateDay(k, { open: e.target.value })}
                    className="rounded-lg border border-bordeaux/20 px-3 py-1.5 outline-none focus:border-bordeaux"
                  />
                  <span className="text-stone-400">–</span>
                  <input
                    data-testid={`admin-hours-close-${k}`}
                    type="time"
                    value={d.close}
                    onChange={(e) => updateDay(k, { close: e.target.value })}
                    className="rounded-lg border border-bordeaux/20 px-3 py-1.5 outline-none focus:border-bordeaux"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 rounded-2xl border border-bordeaux/15 bg-white p-5 sm:p-7">
        <label className="flex items-center gap-3">
          <input
            data-testid="admin-force-closed-toggle"
            type="checkbox"
            checked={settings.force_closed}
            onChange={(e) => setSettings((s) => ({ ...s, force_closed: e.target.checked }))}
            className="h-4 w-4 accent-[#AB182B]"
          />
          <span className="font-display text-lg font-medium text-ink">Chiusura straordinaria (es. ferie)</span>
        </label>
        {settings.force_closed && (
          <input
            data-testid="admin-closing-note-input"
            type="text"
            value={settings.closing_note}
            onChange={(e) => setSettings((s) => ({ ...s, closing_note: e.target.value }))}
            placeholder="Es. Chiusi per ferie dal 10 al 24 agosto"
            className="mt-4 w-full rounded-xl border border-bordeaux/20 px-4 py-3 text-sm outline-none focus:border-bordeaux"
          />
        )}
      </div>

      <button
        data-testid="admin-hours-save-button"
        onClick={save}
        disabled={saving}
        className="mt-8 rounded-full bg-bordeaux px-10 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-ivory transition-transform duration-300 hover:scale-[1.03] disabled:opacity-60"
      >
        {saving ? "Salvataggio…" : "Salva orari"}
      </button>
    </div>
  );
}
